import React, { useState, useEffect, useRef } from 'react'
import { GoogleMap, MarkerF, Marker, useLoadScript } from '@react-google-maps/api'
import { toAbsoluteUrl } from '../../../../../../_metronic/helpers'
import { useLocation } from 'react-router-dom'
import { MixedWidget102 } from '../../../../../../_metronic/partials/widgets/mixed/MixedWidget102'
import { CardsWidget6 } from '../../../../../../_metronic/partials/content/cards/Card6'
import { CardsWidget17 } from '../../../../../../_metronic/partials/content/cards/Card7'
import { OverlayView } from '@react-google-maps/api'
import { useAuth } from '../../../../auth'
import StatsCard2 from '../leaderboard/StatsCards2'

const libraries: 'places'[] = ['places']
const defaultCenter = {
  lat: 37.970833,
  lng: 23.72611,
}

interface LocationData {
  id: number | string
  name: string
  lat: number
  lng: number
  street?: string
  city?: string
  country?: string
  image?: string
  address?: string
  description?: string
  pid?: string | number
  isCustomer?: boolean
  kycDocument?: any[]
  totalDepositedQuantity?: number
  customerType?: string
  mobile?: string
  [key: string]: any
}

function MapComponentC({ zooms = 7, data = [], widgetData }: any) {
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null)
  const [hoveredLocation, setHoveredLocation] = useState<LocationData | null>(null)
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 })
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [analyticsData, setAnalyticsData] = useState<any>(null)
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false)
  const [mapCenter, setMapCenter] = useState(defaultCenter)
  const [isHoverActive, setIsHoverActive] = useState(true)
  const mapRef = useRef<google.maps.Map | null>(null)
  const { currentUser } = useAuth()
  const [customerData, setCustomerData] = useState<any>(null)

  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);

  // FILTER STATES
  const [filterType, setFilterType] = useState<'all' | 'hubs' | 'customers'>('all')
  const [isFiltering, setIsFiltering] = useState(false)

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: 'AIzaSyD32soTrhrAXzmKQp7rH8NsL8NQMhmdqlY',
    libraries,
  })

  const newData = data.map((x, ind) => {
    return {
      id: x.id || ind,
      name: x.name,
      lat: x.position?.lat || 0,
      lng: x.position?.lng || 0,
      isCustomer: x.isCustomer === true,  // STRICT BOOLEAN - convert to true or false
      kycDocument: x.kycDocument || [],
      totalDepositedQuantity: x.totalDepositedQuantity || 0,
      customerType: x.customerType || '',
      mobile: x.mobile || '',
      address: x.address || '',
      ...x,
    }
  })

  // FILTER HANDLER
  const handleFilterChange = (type: 'all' | 'customers' | 'hubs') => {
    if (isFiltering) return
    setIsFiltering(true)
    setFilterType(type)
    setTimeout(() => {
      setIsFiltering(false)
    }, 300)
  }

  // GET DISPLAY DATA - CALCULATE ON EVERY RENDER
  let displayData = newData

  if (filterType === 'all') {
    displayData = newData
  } else if (filterType === 'customers') {
    displayData = newData.filter(loc => loc.isCustomer === true)
  } else if (filterType === 'hubs') {
    displayData = newData.filter(loc => loc.isCustomer === false)
  }

  // DEBUG LOGS
  console.log('=== MAP FILTER DEBUG ===')
  console.log('filterType:', filterType)
  console.log('newData.length:', newData.length)
  console.log('newData items:', newData.map(d => ({ name: d.name, isCustomer: d.isCustomer })))
  console.log('displayData.length:', displayData.length)
  console.log('displayData items:', displayData.map(d => ({ name: d.name, isCustomer: d.isCustomer })))

  console.log('=== FILTER DEBUG ===')
  console.log('filterType:', filterType)
  console.log('newData count:', newData.length, newData.map(d => ({ name: d.name, isCustomer: d.isCustomer })))
  console.log('displayData count:', displayData.length, displayData.map(d => ({ name: d.name, isCustomer: d.isCustomer })))

  useEffect(() => {
    if (newData.length > 0 && mapCenter === defaultCenter) {
      const firstLocation = newData[0]
      if (firstLocation.lat && firstLocation.lng) {
        setMapCenter({ lat: firstLocation.lat, lng: firstLocation.lng })
      }
    }
  }, [newData, mapCenter])

  const { pathname } = useLocation()
  const containerStyle = {
    width: pathname.includes('collect-orders') ? '800px' : '100%',
    height: pathname.includes('collect-orders') ? '600px' : '700px',
  }

  const getMarkerIcon = (isCustomer: boolean) => {
    if (typeof google !== 'undefined' && google.maps) {
      const iconUrl = isCustomer ? '/icons8-man-64.png' : '/mapIcon.png'
      return {
        url: iconUrl,
        scaledSize: new google.maps.Size(50, 50),
        origin: new google.maps.Point(0, 0),
        anchor: new google.maps.Point(0, 0),
      }
    }
    return {}
  }

  const handleMarkerClick = async (location: LocationData) => {

    if (location.isCustomer) {
      console.log("Opening customer modal with:", location)
      setSelectedCustomer(location);
      setShowCustomerModal(true);
      setIsHoverActive(false);
      setHoveredLocation(null);

      // Add API call here
      if (location.id) {
        setIsLoadingAnalytics(true);

        try {
          const authData = localStorage.getItem('kt-auth-react-v')
          const token = authData ? JSON.parse(authData).data.token : null
          const pickupPointId = authData ? JSON.parse(authData).data.franchiseId : null // or however you get pickupPointId

          const response = await fetch(
            `${process.env.REACT_APP_BASE_API_URL}map/analytics?customerId=${location.id}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'pickupPointId': pickupPointId  // Add this header
              }
            }
          );

          if (response.ok) {
            const data = await response.json();
            console.log("Customer analytics:", data);
            // Store in state or use directly
            setCustomerData(data);
          }
        } catch (error) {
          console.error('Error fetching customer analytics:', error);
        } finally {
          setIsLoadingAnalytics(false);
        }
      }

      return;
    }


    if (location.isCustomer) {
      console.log("Opening customer modal with:", location)
      setSelectedCustomer(location);
      setShowCustomerModal(true);
      setIsHoverActive(false);
      setHoveredLocation(null);
      return;
    }

    // Otherwise show facility modal
    setSelectedLocation(location)
    setIsModalOpen(true)
    setIsHoverActive(false)
    setHoveredLocation(null)

    if (location.id) {
      setIsLoadingAnalytics(true)
      setAnalyticsData(null)

      try {
        const pickupPointId = location.id

        const authData = localStorage.getItem('kt-auth-react-v')
        const token = authData ? JSON.parse(authData).data.token : null

        const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}map/analytics?pickupPointId=${pickupPointId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const data = await response.json()
          setAnalyticsData(data)
        } else {
          console.error('Failed to fetch analytics data:', response.statusText)
        }
      } catch (error) {
        console.error('Error fetching analytics data:', error)
      } finally {
        setIsLoadingAnalytics(false)
      }
    }
  }

  const handleMarkerHover = (location: LocationData, event: any) => {
    if (location.isCustomer && !isModalOpen && !showCustomerModal && isHoverActive) {
      setHoveredLocation(location)
      const rect = event.domEvent.currentTarget.getBoundingClientRect()
      setHoverPosition({
        x: event.domEvent.clientX,
        y: event.domEvent.clientY
      })
    }
  }

  const handleMarkerMouseOut = () => {
    if (!isModalOpen && !showCustomerModal) {
      setHoveredLocation(null)
    }
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedLocation(null)
    setCurrentSlide(0)
    setAnalyticsData(null)
    setIsLoadingAnalytics(false)

    setTimeout(() => {
      setIsHoverActive(true)
    }, 300)
  }

  const closeCustomerModal = () => {
    setShowCustomerModal(false);
    setSelectedCustomer(null);

    setTimeout(() => {
      setIsHoverActive(true)
    }, 300)
  }

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % widgetConfigs.length)
  }
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + widgetConfigs.length) % widgetConfigs.length)
  }

  const goToSlide = (index: number) => {
    setCurrentSlide(index)
  }

  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map
  }

  const transformChartData = (weeklyData) => {
    if (!weeklyData) return undefined;

    const transformed = {};
    Object.entries(weeklyData).forEach(([dateKey, value]) => {
      const yearMonth = dateKey.substring(0, 7);
      transformed[yearMonth] = (transformed[yearMonth] || 0) + value;
    });

    return transformed;
  };
  const transformChartDataForCustomer = (weeklyData) => {
    if (!weeklyData) return undefined;
    return weeklyData;
  };


  // const widgetConfigs = [
  //   {
  //     className: 'card-xl-stretch mb-xl-8',
  //     chartColor: 'info',
  //     chartHeight: '150px',
  //     title: "Plastic Prevented",
  //     subtitle: "Quantity collected from waste suppliers",
  //     amount: `${Number(analyticsData?.data?.totalWeeklyCollection || 0).toFixed(2)} kg`,
  //     chartData: analyticsData?.data?.weeklyCollection
  //   },
  //   {
  //     className: 'card-xl-stretch mb-xl-8',
  //     chartColor: 'warning',
  //     chartHeight: '150px',
  //     title: "Plastic Recycled",
  //     subtitle: "Quantity converted into recycled products",
  //     amount: `${Number(analyticsData?.data?.totalWeeklyRecycle || 0).toFixed(2)} kg`,
  //     chartData: analyticsData?.data?.weeklyRecycle
  //   },
  //   {
  //     className: 'card-xl-stretch mb-5 mb-xl-8',
  //     chartColor: 'primary',
  //     chartHeight: '150px',
  //     title: "Plastic Sold",
  //     subtitle: "Quantity converted into recycled products",
  //     amount: `${Number(analyticsData?.data?.totalWeeklyConvert || 0).toFixed(2)} kg`,
  //     chartData: analyticsData?.data?.weeklyConvert
  //   }
  // ]


  const widgetConfigs = (currentUser as any)?.userType === 'SPONSOR' && pathname.includes('/My-impact') ? [
    {
      className: 'card-xl-stretch mb-xl-8',
      chartColor: 'info',
      chartHeight: '150px',
      title: "Plastic Prevented",
      subtitle: "Quantity collected from Households",
      amount: `${Number(analyticsData?.data?.totalWeeklyCollection || 0).toFixed(2)} kg`,
      chartData: analyticsData?.data?.weeklyCollection
    }
  ] : [
    {
      className: 'card-xl-stretch mb-xl-8',
      chartColor: 'info',
      chartHeight: '150px',
      title: "Plastic Prevented",
      subtitle: "Quantity collected from waste suppliers",
      amount: `${Number(analyticsData?.data?.totalWeeklyCollection || 0).toFixed(2)} kg`,
      chartData: analyticsData?.data?.weeklyCollection
    },
    {
      className: 'card-xl-stretch mb-xl-8',
      chartColor: 'warning',
      chartHeight: '150px',
      title: "Plastic Recycled",
      subtitle: "Quantity converted into recycled products",
      amount: `${Number(analyticsData?.data?.totalWeeklyRecycle || 0).toFixed(2)} kg`,
      chartData: analyticsData?.data?.weeklyRecycle
    },
    {
      className: 'card-xl-stretch mb-5 mb-xl-8',
      chartColor: 'primary',
      chartHeight: '150px',
      title: "Plastic Sold",
      subtitle: "Quantity converted into recycled products",
      amount: `${Number(analyticsData?.data?.totalWeeklyConvert || 0).toFixed(2)} kg`,
      chartData: analyticsData?.data?.weeklyConvert
    }
  ]

  if (!isLoaded) return <div>Loading...</div>
  if (loadError) return <div>Error loading maps</div>

  return (
    <div className='App'>
      {/* FILTER BUTTONS */}
      <div style={{
        display: 'flex',
        gap: '8px',
        // marginBottom: '10px',
      }}>
        {/* <button
          onClick={() => handleFilterChange('all')}
          disabled={isFiltering}
          style={{
            padding: '10px 14px',
            borderRadius: '4px',
            border: filterType === 'all' ? '2px solid #4f45f5' : '1px solid #ddd',
            cursor: isFiltering ? 'not-allowed' : 'pointer',
            backgroundColor: filterType === 'all' ? '#4f45f5' : '#ffffff',
            color: filterType === 'all' ? 'white' : '#333',
            fontWeight: '600',
            fontSize: '13px',
            opacity: isFiltering ? 0.6 : 1,
            transition: 'all 0.3s ease'
          }}
        >
          All
        </button> */}
        {/* <button
          onClick={() => handleFilterChange('hubs')}
          disabled={isFiltering}
          style={{
            padding: '10px 14px',
            borderRadius: '4px',
            border: filterType === 'hubs' ? '2px solid #4f45f5' : '1px solid #ddd',
            cursor: isFiltering ? 'not-allowed' : 'pointer',
            backgroundColor: filterType === 'hubs' ? '#4f45f5' : '#ffffff',
            color: filterType === 'hubs' ? 'white' : '#333',
            fontWeight: '600',
            fontSize: '13px',
            opacity: isFiltering ? 0.6 : 1,
            transition: 'all 0.3s ease'
          }}
        >
          Hubs
        </button> */}
        {/* <button
          onClick={() => handleFilterChange('customers')}
          disabled={isFiltering}
          style={{
            padding: '10px 14px',
            borderRadius: '4px',
            border: filterType === 'customers' ? '2px solid #4f45f5' : '1px solid #ddd',
            cursor: isFiltering ? 'not-allowed' : 'pointer',
            backgroundColor: filterType === 'customers' ? '#4f45f5' : '#ffffff',
            color: filterType === 'customers' ? 'white' : '#333',
            fontWeight: '600',
            fontSize: '13px',
            opacity: isFiltering ? 0.6 : 1,
            transition: 'all 0.3s ease'
          }}
        >
          Household
        </button> */}
      </div>

      <GoogleMap
        key={filterType}
        mapContainerStyle={containerStyle}
        mapContainerClassName='map-container'
        center={mapCenter}
        zoom={zooms}
        onLoad={onMapLoad}
        options={{
          disableDefaultUI: false,
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
        }}
      >
        {displayData.map((location) => {
          if (location.isCustomer && (currentUser as any)?.userType !== 'SPONSOR') {
            return (
              <OverlayView
                key={location.id}
                position={{ lat: location.lat, lng: location.lng }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div
                  onClick={() => handleMarkerClick(location)}
                  onMouseOver={(e) => {
                    if (location.isCustomer && !isModalOpen && !showCustomerModal && isHoverActive) {
                      setHoveredLocation(location);
                      setHoverPosition({
                        x: e.clientX,
                        y: e.clientY
                      });
                    }
                  }}
                  onMouseOut={() => {
                    if (!isModalOpen && !showCustomerModal) {
                      setHoveredLocation(null);
                    }
                  }}
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '3px solid white',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    cursor: 'pointer',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'white'
                  }}
                >
                  <img
                    src={
                      location.kycDocument?.[0]?.docUrl ||
                      toAbsoluteUrl('/media/avatars/blank.png')
                    }
                    alt={location.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
              </OverlayView>
            )
          } else {
            return (
              <Marker
                key={location.id}
                position={{ lat: location.lat, lng: location.lng }}
                title={location.name}
                onClick={() => handleMarkerClick(location)}
                icon="/mapIcon.png"
              />
            )
          }
        })}
      </GoogleMap>

      {/* Hover Card for Customers */}
      {hoveredLocation && hoveredLocation.isCustomer && (
        <div
          className="hover-card"
          style={{
            position: 'fixed',
            left: hoverPosition.x + 10,
            top: hoverPosition.y - 80,
            zIndex: 1001,
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            minWidth: '200px',
            maxWidth: '300px',
            pointerEvents: 'none'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              overflow: 'hidden',
              flexShrink: 0,
              backgroundColor: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {hoveredLocation.kycDocument?.[0]?.docUrl ? (
                <img
                  src={hoveredLocation.kycDocument[0].docUrl}
                  alt={hoveredLocation.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <span style={{ fontSize: '20px', color: '#999' }}>👤</span>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
                {hoveredLocation.name}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Customer Details Modal */}
      {showCustomerModal && selectedCustomer && (
        <div className="modal-overlay" onClick={closeCustomerModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Household Details</h3>
              <button className="close-button" onClick={closeCustomerModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="location-info">
                <div className="location-details">
                  <div className="location-image">
                    <img
                      src={
                        selectedCustomer.kycDocument?.[0]?.docUrl ||
                        toAbsoluteUrl('media/avatars/blank.png')
                      }
                      alt={selectedCustomer.name}
                      className="location-img"
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <p style={{ display: 'flex', margin: 0 }}>
                      <strong style={{ minWidth: '120px', marginRight: '2px' }}>Name:</strong>
                      <span>{selectedCustomer?.name || 'N/A'}</span>
                    </p>
                    <p style={{ display: 'flex', margin: 0 }}>
                      <strong style={{ minWidth: '120px', marginRight: '2px' }}>Status:</strong>
                      <span
                        className={customerData?.data?.active ? 'badge badge-light-success fw-bold' : 'badge fw-bold'}
                        style={!customerData?.data?.active ? { backgroundColor: '#fff8dd', color: '#fbd124' } : undefined}
                      >
                        {customerData?.data?.active ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </p>







                    {/* <p style={{ display: 'flex', margin: 0 }}>
                      <strong style={{ minWidth: '120px', marginRight: '2px' }}>Mobile:</strong>
                      <span>{selectedCustomer?.mobile || 'N/A'}</span>
                    </p>
                    <p style={{ display: 'flex', margin: 0 }}>
                      <strong style={{ minWidth: '120px', marginRight: '2px' }}>Address:</strong>
                      <span>{customerData?.data?.address?.street}</span>
                    </p> */}



                    {(currentUser as any)?.userType !== 'SPONSOR' && (
                      <>
                        <p style={{ display: 'flex', margin: 0 }}>
                          <strong style={{ minWidth: '120px', marginRight: '2px' }}>Mobile:</strong>
                          <span>{selectedCustomer?.mobile || 'N/A'}</span>
                        </p>
                        <p style={{ display: 'flex', margin: 0 }}>
                          <strong style={{ minWidth: '120px', marginRight: '2px' }}>Address:</strong>
                          <span>{customerData?.data?.address?.street}</span>
                        </p>
                      </>
                    )}


                  </div>
                </div>
              </div>

              {/* Customer 4 Cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '20px' }}>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{
                    flex: 1,
                    backgroundColor: '#ffffff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '80px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>daily Avg</div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#4f45f5' }}>
                      {Number(customerData?.data?.avgDailyCollected)?.toFixed(2)}
                    </div>
                  </div>
                  <div style={{
                    flex: 1,
                    backgroundColor: '#ffffff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '80px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>Monthly avg</div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#ffe500' }}>
                      {Number(customerData?.data?.avgMonthlyCollected)?.toFixed(2)}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{
                    flex: 1,
                    backgroundColor: '#ffffff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '80px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>Annual avg</div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#f4b7f9' }}>
                      {Number(customerData?.data?.avgYearlyCollected)?.toFixed(2)}
                    </div>
                  </div>
                  <div style={{
                    flex: 1,
                    backgroundColor: '#ffffff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '80px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>Lifetime</div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#ed6e43' }}>
                      {Number(customerData?.data?.lifeTimePerformance)?.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <MixedWidget102
              className=''
              chartColor='info'
              chartHeight='150px'
              title="Collection trend"
              subtitle="Last 7 days"
              amount={``}
              chartData={transformChartDataForCustomer(customerData?.data?.weeklyCollection)}
              seriesName="Quantity"
            />


            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeCustomerModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Facility Modal (with analytics) */}
      {isModalOpen && selectedLocation && !selectedLocation.isCustomer && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h3 style={{ margin: 0 }}>{selectedLocation.name}</h3>
                {analyticsData?.data && (
                  <span
                    className={analyticsData?.data?.active ? 'badge badge-light-success fw-bold' : 'badge fw-bold'}
                    style={!analyticsData?.data?.active ? { backgroundColor: '#fff8dd', color: '#fbd124' } : undefined}
                  >
                    {analyticsData?.data?.active ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                )}
              </div>
              <button className="close-button" onClick={closeModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="location-info">
                <div className="location-details">
                  <div className="location-image">
                    <img
                      src={
                        analyticsData?.data?.kycDocument?.docUrl ||
                        selectedLocation.image ||
                        toAbsoluteUrl('media/avatars/blank.png')
                      }
                      alt={selectedLocation.name}
                      className="location-img"
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <p style={{ display: 'flex', margin: 0 }}>
                      <strong style={{ minWidth: '80px', marginRight: '2px' }}>Address:</strong>
                      <span>{analyticsData?.data?.address?.street}</span>
                    </p>
                  </div>
                </div>
              </div>

              {isLoadingAnalytics && (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  Loading analytics data...
                </div>
              )}

              {!isLoadingAnalytics && analyticsData && (
                <>
                  <div className='row mt-5'>
                    <div className='col-12 col-md-6 d-flex mb-3'>
                      {/* <CardsWidget6
                        className='w-100'
                        color='#F1416C'
                        img={toAbsoluteUrl('/media/patterns/vector-1.png')}
                        count={(analyticsData?.data?.linkedCustomerCount || 0).toFixed(2)}
                        activePercentage={(analyticsData?.data?.participationRate || 0).toFixed(2)}
                        progressPercentage={(analyticsData?.data?.participationRate || 0).toFixed(2)}
                      /> */}



                      <StatsCard2
                        className='w-100'
                        targetCustomerCount={(analyticsData?.data?.linkedCustomerCount || 0)}
                        actualCustomerCount={analyticsData?.data?.activeCustomers}
                        householdpercentage={Number(((analyticsData?.data?.participationRate || 0) * 100).toFixed(2))}
                      />


                    </div>
                    <div className='col-12 col-md-6 d-flex mb-3'>
                      <CardsWidget17 className='w-100' lifetimeCollection={(analyticsData?.data?.lifeTimePerformance || 0).toFixed(2)} />
                    </div>
                  </div>

                  <div className="carousel-container">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
                      <div style={{ display: 'flex', gap: '16px' }}>
                        <div style={{
                          flex: 1,
                          backgroundColor: '#ffffff',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          padding: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          height: '80px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>Daily avg</div>
                          <div style={{ fontSize: '24px', fontWeight: '700', color: '#4f45f5' }}>
                            {Number(analyticsData?.data?.avgDailyCollected || 0).toFixed(2)}
                          </div>
                        </div>
                        <div style={{
                          flex: 1,
                          backgroundColor: '#ffffff',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          padding: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          height: '80px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>Weekly avg</div>
                          <div style={{ fontSize: '24px', fontWeight: '700', color: '#ffe500' }}>
                            {Number(analyticsData?.data?.avgWeeklyCollected || 0).toFixed(2)}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '16px' }}>
                        <div style={{
                          flex: 1,
                          backgroundColor: '#ffffff',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          padding: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          height: '80px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>Monthly avg</div>
                          <div style={{ fontSize: '24px', fontWeight: '700', color: '#f4b7f9' }}>
                            {Number(analyticsData?.data?.avgMonthlyCollected || 0).toFixed(2)}
                          </div>
                        </div>
                        <div style={{
                          flex: 1,
                          backgroundColor: '#ffffff',
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          padding: '16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          height: '80px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>Lifetime</div>
                          <div style={{ fontSize: '24px', fontWeight: '700', color: '#ed6e43' }}>
                            {Number(analyticsData?.data?.lifeTimePerformance || 0).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="carousel-header">
                      <h4>Latest Trends</h4>
                      <div className="carousel-controls">
                        <button className="carousel-btn" onClick={prevSlide} disabled={currentSlide === 0}>
                          ←
                        </button>
                        <span className="slide-indicator">
                          {currentSlide + 1}/{widgetConfigs.length}
                        </span>
                        <button
                          className="carousel-btn"
                          onClick={nextSlide}
                          disabled={currentSlide === widgetConfigs.length - 1}
                        >
                          →
                        </button>
                      </div>
                    </div>

                    <div className="carousel-slides">
                      <div
                        className="carousel-track"
                        style={{
                          transform: `translateX(-${currentSlide * 100}%)`,
                        }}
                      >
                        {widgetConfigs.map((config, index) => (
                          <div key={index} className="carousel-slide">
                            <MixedWidget102
                              {...config}
                              chartData={transformChartData(config.chartData)}
                              seriesName="Quantity"
                              color="#4f45f5"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="carousel-dots">
                      {widgetConfigs.map((_, index) => (
                        <button
                          key={index}
                          className={`dot ${index === currentSlide ? 'active' : ''}`}
                          onClick={() => goToSlide(index)}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .hover-card {
          animation: fadeIn 0.2s ease-in-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          border-radius: 8px;
          max-width: 600px;
          width: 95%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #eee;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 1.25rem;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #666;
          padding: 0;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-button:hover {
          color: #000;
        }

        .modal-body {
          padding: 20px;
        }

        .modal-body p {
          margin-bottom: 10px;
        }

        .location-info {
          margin-bottom: 20px;
          padding-bottom: 20px;
          border-bottom: 1px solid #eee;
        }

        .location-details {
          display: flex;
          align-items: flex-start;
          gap: 20px;
        }

        .location-image {
          flex-shrink: 0;
        }

        .location-img {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid #e9ecef;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .location-text {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 6px;
        }

        .location-field {
          margin: 0;
          font-size: 14px;
          line-height: 1.3;
        }

        .location-field strong {
          font-weight: 600;
          color: #2c3e50;
        }

        .carousel-container {
          margin-top: 20px;
        }

        .carousel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }

        .carousel-header h4 {
          margin: 0;
          font-size: 1.1rem;
        }

        .carousel-controls {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .carousel-btn {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }

        .carousel-btn:hover {
          background: #e9ecef;
          border-color: #adb5bd;
        }

        .carousel-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .slide-indicator {
          font-size: 13px;
          color: #6c757d;
          min-width: 35px;
          text-align: center;
        }

        .carousel-slides {
          overflow: hidden;
          border-radius: 8px;
          margin-bottom: 15px;
        }

        .carousel-track {
          display: flex;
          transition: transform 0.3s ease;
        }

        .carousel-slide {
          min-width: 100%;
          padding: 10px;
          box-sizing: border-box;
        }

        .carousel-dots {
          display: flex;
          justify-content: center;
          gap: 8px;
        }

        .dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          border: none;
          background: #dee2e6;
          cursor: pointer;
          transition: background 0.2s;
        }

        .dot.active {
          background: #007bff;
        }

        .dot:hover {
          background: #adb5bd;
        }

        .dot.active:hover {
          background: #0056b3;
        }

        .modal-footer {
          padding: 20px;
          border-top: 1px solid #eee;
          text-align: right;
        }

        .btn {
          padding: 6px 14px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 13px;
        }

        .btn-secondary {
          background-color: #6c757d;
          color: white;
        }

        .btn-secondary:hover {
          background-color: #545b62;
        }
      `}</style>
    </div>
  )
}

export default MapComponentC